import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { SECOND } from '~/constants/variables';
import { useModal } from '~/hooks/useModal';
import { usePickupAddress } from '~/hooks/usePickupAddress';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';

import { cartStore, mainStore, userStore } from '~/stores';

import Fallback from './Fallback';
import modalMap from './modalMap';

const ModalList = () => {
  const [searchParams] = useSearchParams();
  const { openModal } = useModal();

  usePickupAddress(searchParams);

  const modalType = searchParams.get('modal') as Nullable<ModalType>;

  const location = useLocation();

  const ageRestrictionModal =
    !userStore.personalData.isAdult &&
    location.pathname !== '/legals' &&
    !company.missAgeRestrictionModal &&
    (!company.hasAgeRestriction || cartStore.isAdultCommodityInCart);

  useEffect(() => {
    if (!ageRestrictionModal || modalType) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!company.newDesignSystemEnabled) {
        mainStore.sendToRN('setStatusBar', 'light-content');
      }
      openModal(ModalType.AgeRestriction);
    }, SECOND * 10);

    return () => {
      clearTimeout(timeout);
    };
  }, [ageRestrictionModal, modalType, openModal]);

  const modal = useMemo(
    () => (modalType ? modalMap.get(modalType) : undefined),
    [modalType],
  );

  useStatusBarControl({
    initialColor: modal?.statusColor || 'dark-content',
    disableScrollDetection: true,
    enabled: !!modal,
  });

  if (!modal) {
    return null;
  }

  const ModalComponent = modal?.component;

  return (
    <Suspense fallback={<Fallback />}>
      <ModalComponent isShow />
    </Suspense>
  );
};

export default observer(ModalList);
